import { Component, OnDestroy, OnInit } from "@angular/core";
import { OAuthService } from "angular-oauth2-oidc";
import { SessionService } from "./services/session.service";
import { Router } from "@angular/router";
import { InactivityService } from "@services/inactivity.service";
import { SessionTimerService } from "@services/session-expiracion.service";
import { ExtenderSesionService } from "@services/extender-sesion.service";
import { AutoExtenderSesionService } from "@services/auto-extender-sesion.service";
import { ErrorService } from "@services/error.service";
import { SessionCaducadaService } from "@services/session-caducada.service";
import { Mensajes } from "@shared/enums/mensajes.enum";
import { TerminologiaService } from "@services/terminologia.service";
import { Subject, takeUntil } from "rxjs";

@Component({
	selector: "app-root",
	templateUrl: "./app.component.html",
	styleUrls: ["./app.component.scss"],
})
export class AppComponent implements OnInit, OnDestroy {
	sidebarOpen = true;
	mostrarModalInactividad: boolean = false;
	prolongandoSesion: boolean = false;
	sesionCaducada: boolean = false;

	private destroy$ = new Subject<void>();

	constructor(
		private oauthService: OAuthService,
		private sessionService: SessionService,
		private router: Router,
		private inactivityService: InactivityService,
		private sessionTimerService: SessionTimerService,
		private extenderSesionService: ExtenderSesionService,
		private autoExtenderSesionService: AutoExtenderSesionService,
		private errorService: ErrorService,
		private sessionCaducadaService: SessionCaducadaService,
		private terminologiaService: TerminologiaService
	) {
		this.configureOAuth();
	}

	ngOnInit() {
		// Activa/desactiva los servicios según exista o no sesión.
		this.sessionService.sesion$
			.pipe(takeUntil(this.destroy$))
			.subscribe((sesion) => {
				if (sesion) {
					this.inactivityService.startWatching();
					this.autoExtenderSesionService.start();
				} else {
					this.inactivityService.eliminarEventos();
					this.autoExtenderSesionService.stop();
					this.mostrarModalInactividad = false;
				}
			});

		// Timer basado en el campo `expiracion` de la sesión.
		this.sessionTimerService.watchSession();

		// Pre-aviso (1 min antes): muestra el modal para ofrecer prolongar o cerrar.
		this.sessionTimerService.sessionExpiring$
			.pipe(takeUntil(this.destroy$))
			.subscribe(() => {
				if (this.sessionService.sesionEnConflicto) {
					// En conflicto: la sesión debe expirar naturalmente, sin modal.
					return;
				}
				this.mostrarModalInactividad = true;
			});

		// Si la sesión expira sin respuesta del usuario, cerrarla.
		this.sessionTimerService.sessionExpired$
			.pipe(takeUntil(this.destroy$))
			.subscribe(() => {
				this.mostrarModalInactividad = false;
				if (this.sessionService.isAuthenticated()) {
					this.sessionService.logout();
				}
			});

		// Backend reporta sesión caducada: bloquear la UI con modal y detener timers.
		this.sessionCaducadaService.caducada$
			.pipe(takeUntil(this.destroy$))
			.subscribe((caducada) => {
				this.sesionCaducada = caducada;
				if (caducada) {
					this.mostrarModalInactividad = false;
					this.inactivityService.eliminarEventos();
					this.autoExtenderSesionService.stop();
					this.sessionTimerService.stop();
				}
			});
	}

	ngOnDestroy() {
		this.destroy$.next();
		this.destroy$.complete();
		this.inactivityService.eliminarEventos();
		this.autoExtenderSesionService.stop();
		this.sessionTimerService.stop();
	}

	get mostrarCambioPerfil(): boolean {
		let mostrar: boolean = true;
		["/inicio", "/unauthorized", "/login/callback", "/ingresar"].forEach(
			(url) => {
				if (this.router.url.startsWith(url)) mostrar = false;
			}
		);
		return mostrar;
	}

	get mostrarBreadCrumbs(): boolean {
		let mostrar: boolean = true;
		["/login/callback", "/ingresar"].forEach((url) => {
			if (this.router.url.startsWith(url)) mostrar = false;
		});
		return mostrar;
	}

	get isLoadingChangeRole(): boolean {
		return this.sessionService.isLoadingChangeRole;
	}

	get mensajeLogoutInactividad(): string {
		const mensaje = this.terminologiaService.obtenerMensajePorID(
			Mensajes.logout_inactividad
		);
		return mensaje;
	}

	private configureOAuth() {
		this.oauthService.configure({
			loginUrl: "https://accounts.claveunica.gob.cl/openid/authorize/",
			tokenEndpoint: "https://accounts.claveunica.gob.cl/oauth2/token",
			userinfoEndpoint:
				"https://accounts.claveunica.gob.cl/openid/userinfo",
			clientId: "4a94478dd25f4430a0a023b87e11170d",
			redirectUri: "https://milicenciamedica.cl/",
			responseType: "code",
			scope: "openid run name",
			oidc: true,
			requestAccessToken: true,
		});
	}

	toggleSidebar(): void {
		this.sidebarOpen = !this.sidebarOpen;
		const sidebar = document.querySelector(".sidebar");
		if (sidebar) {
			sidebar.classList.toggle("sidebar-open", this.sidebarOpen);
			(sidebar as HTMLElement).style.transform = this.sidebarOpen
				? "translateX(0)"
				: "";
		}
	}

	prolongarSesion(): void {
		if (this.prolongandoSesion) return;
		if (this.sessionService.sesionEnConflicto) {
			// En conflicto: no debe prolongarse, la sesión debe expirar naturalmente.
			this.mostrarModalInactividad = false;
			return;
		}
		this.prolongandoSesion = true;

		const minutos = 10;

		this.extenderSesionService
			.extenderSesion<any>(minutos)
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: (response) => {
					if (!response?.success) {
						this.prolongandoSesion = false;
						this.errorService.handle(
							new Error(response?.msg || "Respuesta inesperada al prolongar sesión"),
							"No se pudo prolongar la sesión."
						);
						return;
					}

					// El backend solo confirma. Calculamos localmente la nueva expiración.
					const sesion = this.sessionService.getSesionActual();
					const actual = sesion?.expiracion ? new Date(sesion.expiracion).getTime() : Date.now();
					const nuevaExpiracion = new Date(actual + minutos * 60 * 1000).toISOString();

					this.sessionService.actualizarFechasSesion(nuevaExpiracion);
					this.inactivityService.resetear();

					this.mostrarModalInactividad = false;
					this.prolongandoSesion = false;
				},
				error: (err) => {
					this.prolongandoSesion = false;
					this.errorService.handle(err, "No se pudo prolongar la sesión.");
				},
			});
	}

	cerrarSesion(): void {
		this.mostrarModalInactividad = false;
		this.sessionService.logout();
	}
}
